import { Api } from '@evertel/api'
import { slice, sum } from 'lodash'
import { NormalizedMedia } from './NormalizedMediaType'

const CHUNK_SIZE = 1024 * 1024 * 5 // AWS minimum part size is 5mb for all parts except the last
const INITIAL_PARALLEL_UPLOADS = 2
let stableParallelCount = null // Global to persist between uploads
const MAX_PARALLEL_UPLOADS = 5  // Reduced to prevent browser connection limits
const MAX_RETRY = 3
const SPEED_CHANGE_THRESHOLD = 15 // 15% change threshold for adjusting parallel uploads
const MIN_UPLOAD_TIME_HISTORY = 3 // Minimum number of uploads before adjusting parallelism
const FORCE_INCREASE_INTERVAL = 7 // Force an increase every N chunks

export type MediaUploadModelName =
  | 'RoomMessages'
  | 'ThreadMessages'
  | 'RoomMessage'
  | 'ThreadMessage'
  | 'Document'
  | 'Documents'
  | 'DocumentSchema'
  | 'Departments'
  | 'BlueUsers'
  | 'Bots'
  | 'Integrations'

export type AsyncGetPartFormDataFn = (partNumber: number, start: number, end: number) => Promise<FormData>
export type AsyncOnPartCompleteFn = (partNumber: number) => void
export type OnProgressFn = (completed: number, total: number) => void

export interface MultipartUploadableMedia {
    fileName: string
    mimeType: string
    contentLength: number
    meta: any
    computeContentLength?: () => Promise<number>
    onGetPartFormData: AsyncGetPartFormDataFn
    onPartComplete?: AsyncOnPartCompleteFn
    onProgress: OnProgressFn
    modelName: MediaUploadModelName
    modelId: number
    userId: number
    deviceId: number
}

export const Options = {
    maxParallelUploadCount: MAX_PARALLEL_UPLOADS
}

const createSequentialFilePartsArray = (contentLength: number) => {
    const totalPartsCount = Math.ceil(contentLength / CHUNK_SIZE)

    if (totalPartsCount <= 0) {
        return []
    }

    const sequentialArray = Array.from({ length: totalPartsCount }, (_, index) => index + 1)
    return sequentialArray.map(partNumber => {
        const start = (partNumber - 1) * CHUNK_SIZE
        const partContentLength = Math.min(CHUNK_SIZE, contentLength - start)
        return {
            start,
            end: start + partContentLength,
            partContentLength,
            partNumber
        }
    })
}

export async function mediaMultipartUpload(api: Api, media: MultipartUploadableMedia, options = Options) {
    const {
        fileName,
        mimeType,
        contentLength: preComputedContentLength,
        computeContentLength,
        onGetPartFormData,
        onPartComplete,
        onProgress,
        modelName,
        modelId,
        deviceId,
        userId,
        meta = {}
    } = media

    if (!fileName || !mimeType || !api) {
        throw new Error('Required parameters missing: fileName, mimeType or api')
    }

    let currentUploadId = ''
    let mediaMultipartId: number
    let progressCompleted = 0
    const partsProgress: number[] = []
    const uploadTimes: number[] = []

    let contentLength
    if (computeContentLength) {
        contentLength = await computeContentLength()
    } else {
        contentLength = preComputedContentLength
    }


    console.log('mediaMultipartUpload upload', JSON.stringify({ fileName, mimeType, contentLength, meta }))

    const uploadPart = async (
        api: Api,
        partNumber: number,
        partContentLength: number,
        formData: FormData,
        retryCount = 0
    ): Promise<any> => {
        try {
            return await api.Routes.MediaMultiPartUpload.postUploadPart(
                mediaMultipartId,
                partNumber,
                currentUploadId,
                partContentLength,
                formData,
                (event: any) => {
                    try {
                        partsProgress[partNumber] = event.loaded
                        progressCompleted = sum(partsProgress)
                        onProgress?.(progressCompleted, contentLength)
                    } catch (error) {
                        console.error('Progress update error:', error)
                    }
                }
            )
        } catch (error) {
            if (retryCount < MAX_RETRY) {
                const delay = Math.min(1000 * Math.pow(2, retryCount), 10000)
                await new Promise(resolve => setTimeout(resolve, delay))
                return uploadPart(api, partNumber, partContentLength, formData, retryCount + 1)
            }
            throw error
        }
    }

    // Check for existing upload (filter server-side by device and recent uploads only)
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    const existingUpload = await api.Routes.BlueUser.getMediaMultiPartUploads(userId, {
        where: {
            deviceId,
            createdDate: { gte: thirtyDaysAgo }
        },
        order: 'createdDate DESC'
    })
    const previousUpload = existingUpload.find(m =>
        m.fileName === fileName &&
        m.mimetype === mimeType &&
        m.postProcessing?.meta?.modelName === modelName &&
        m.postProcessing?.meta?.modelId === modelId
    )

    let fileParts = createSequentialFilePartsArray(contentLength)

    if (previousUpload) {
        currentUploadId = previousUpload.uploadId
        mediaMultipartId = previousUpload.id

        const successfulParts = await api.Routes.MediaMultiPartUpload.getParts(previousUpload.id)
        const completedPartNumbers = successfulParts.map(p => p.partNumber)

        fileParts.filter(p => completedPartNumbers.includes(p.partNumber))
            .forEach(p => {
                partsProgress[p.partNumber] = p.partContentLength
            })
        fileParts = fileParts.filter(p => !completedPartNumbers.includes(p.partNumber))
    } else {
        const uploadMeta = { ...meta, modelName, modelId }
        const uploadStartRoutes = {
            RoomMessages: () => api.Routes.RoomMessage.postMediaUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            RoomMessage: () => api.Routes.RoomMessage.postMediaUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            ThreadMessages: () => api.Routes.ThreadMessage.postMediaUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            ThreadMessage: () => api.Routes.ThreadMessage.postMediaUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            Document: () => api.Routes.Document.postMediaUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            Documents: () => api.Routes.Document.postMediaUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            DocumentSchema: () => api.Routes.DocumentSchema.postMediaUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            Departments: () => api.Routes.Department.postMediaPublicUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            BlueUsers: () => api.Routes.BlueUser.postMediaPublicUploadStart(modelId, { fileName, mimeType, meta: uploadMeta }),
            Bots: () => api.Routes.Bot.postMediaUploadStart({ fileName, mimeType, meta: uploadMeta }),
            Integrations: () => api.Routes.Integration.postMediaPublicUploadStart(modelId, { fileName, mimeType, meta: uploadMeta })
        }

        try {
            const params = await uploadStartRoutes[modelName]()
            currentUploadId = params?.uploadId
            mediaMultipartId = params?.id
        } catch (error) {
            throw new Error(`Failed to initialize upload: ${error}`)
        }
    }

    let parallelUploadCount = stableParallelCount || INITIAL_PARALLEL_UPLOADS
    let chunksAtCurrentParallel = 0

    for (let index = 0; index < fileParts.length;) {
        const batch = slice(fileParts, index, index + parallelUploadCount)
        index += parallelUploadCount
        const startTime = Date.now()

        try {
            await Promise.all(batch.map(async filePart => {
                const formData = await onGetPartFormData(filePart.partNumber, filePart.start, filePart.end)
                await uploadPart(api, filePart.partNumber, filePart.partContentLength, formData)
                onPartComplete?.(filePart.partNumber)
            }))

            const currentBatchTime = Date.now() - startTime
            uploadTimes.push(currentBatchTime)
            chunksAtCurrentParallel++  // Increment our counter

            // Only adjust parallel uploads after collecting enough timing data
            if (uploadTimes.length >= MIN_UPLOAD_TIME_HISTORY) {
                const avgTime = uploadTimes.slice(-MIN_UPLOAD_TIME_HISTORY).reduce((a, b) => a + b, 0) / MIN_UPLOAD_TIME_HISTORY
                const lastTime = uploadTimes[uploadTimes.length - 1]
                const percentChange = ((lastTime - avgTime) / avgTime) * 100

                console.log(`Upload time change: ${percentChange.toFixed(1)}% (current: ${lastTime}ms, avg: ${avgTime.toFixed(1)}ms)`)

                if (percentChange < -SPEED_CHANGE_THRESHOLD && parallelUploadCount < options.maxParallelUploadCount) {
                    console.log(`Increasing parallel uploads to ${parallelUploadCount + 1}`)
                    parallelUploadCount++
                    chunksAtCurrentParallel = 0  // Reset counter on change
                } else if (percentChange > SPEED_CHANGE_THRESHOLD && parallelUploadCount > 1) {
                    console.log(`Decreasing parallel uploads to ${parallelUploadCount - 1}`)
                    parallelUploadCount--
                    chunksAtCurrentParallel = 0  // Reset counter on change
                } else if (chunksAtCurrentParallel >= FORCE_INCREASE_INTERVAL && parallelUploadCount < options.maxParallelUploadCount) {
                    stableParallelCount = parallelUploadCount
                    console.log(`Forcing increase to test bandwidth improvement after ${FORCE_INCREASE_INTERVAL} chunks`)
                    parallelUploadCount++
                    chunksAtCurrentParallel = 0  // Reset counter after force increase
                }
            }

        } catch (error) {
            throw new Error(`Upload failed: ${error}`)
        }
    }

    return api.Routes.MediaMultiPartUpload.postUploadComplete(mediaMultipartId, currentUploadId)
}
